const MAX_DETECTION_CATEGORIES = 6;

const NEUTRAL_KEYWORDS = new Set([
  '',
  '-',
  '--',
  'NA',
  'N/A',
  'NONE',
  'NULL',
  'UNKNOWN',
  'NOT SET',
  '(NOT SET)',
  'UNDEFINED',
]);

const formatThreatCategoryName = (input) => (
  String(input || '')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
);

export const getThreatCategoryColor = (theme, categoryName, fallbackIndex = 0) => {
  const normalizedName = String(categoryName || '').trim().toLowerCase();
  const colors = theme.threatCategoryColors || {};

  if (normalizedName === 'automation') return colors.automation;
  if (normalizedName === 'known bot framework') return colors.knownBotFramework;
  if (normalizedName === 'browser tampering') return colors.browserTampering;
  if (normalizedName === 'clickjacking') return colors.clickjacking;
  if (normalizedName === 'anti-detect browser') return colors.antiDetectBrowser;
  if (normalizedName === 'known "good" bot') return colors.knownGoodBot;
  if (normalizedName === 'other') return colors.other;

  return theme.categoricalPalette?.[fallbackIndex % theme.categoricalPalette.length] || theme.strokeMid;
};

export const buildThreatCategoryData = (siteData, theme) => {
  const rows = Array.isArray(siteData) ? siteData : [];
  const categories = {};

  for (const entries of rows) {
    for (const entry of Object.values(entries)) {
      const categoryStats = entry[5] || [];

      for (const category of categoryStats) {
        categories[category.type] = (categories[category.type] || 0) + category.count;
      }
    }
  }

  const data = Object.entries(categories)
    .map(([category, count]) => ({
      name: formatThreatCategoryName(category),
      count,
    }))
    .sort((a, b) => b.count - a.count);

  const total = data.reduce((sum, item) => sum + item.count, 0);
  if (!total) return [];

  const visibleItems = data.slice(0, MAX_DETECTION_CATEGORIES - 1);
  const remainingCount = data
    .slice(MAX_DETECTION_CATEGORIES - 1)
    .reduce((sum, item) => sum + item.count, 0);

  const finalItems = remainingCount > 0
    ? [...visibleItems, { name: 'Other', count: remainingCount }]
    : data.slice(0, MAX_DETECTION_CATEGORIES);

  return finalItems.map((item, index) => ({
    ...item,
    value: (item.count / total) * 100,
    color: getThreatCategoryColor(theme, item.name, index),
  }));
};

const getKeywordRiskLevel = (keyword, count) => {
  const normalizedKeyword = String(keyword ?? '').trim().toUpperCase();
  if (NEUTRAL_KEYWORDS.has(normalizedKeyword)) {
    return 'na';
  }
  return count > 10 ? 'high' : 'medium';
};

export const buildKeywordData = (siteData) => {
  const rows = Array.isArray(siteData) ? siteData : [];
  const keywords = {};

  for (const entries of rows) {
    for (const entry of Object.values(entries)) {
      const keywordStats = entry[2] || [];

      for (const keyword of keywordStats) {
        keywords[keyword.text] = (keywords[keyword.text] || 0) + keyword.number;
      }
    }
  }

  return Object.entries(keywords)
    .map(([text, total]) => ({
      text,
      number: total.toLocaleString(),
      riskLevel: getKeywordRiskLevel(text, total),
    }))
    .sort((a, b) => Number.parseInt(b.number.replace(/,/g, ''), 10) - Number.parseInt(a.number.replace(/,/g, ''), 10));
};

export const buildSourceData = (siteData) => {
  const rows = Array.isArray(siteData) ? siteData : [];
  const sources = {};

  for (const entries of rows) {
    for (const entry of Object.values(entries)) {
      const sourceStats = entry[3] || [];

      for (const source of sourceStats) {
        sources[source.source] = (sources[source.source] || 0) + source.count;
      }
    }
  }

  return Object.entries(sources)
    .map(([source, clicks]) => ({
      source,
      rawClicks: clicks,
      clicks: clicks.toLocaleString(),
    }))
    .sort((a, b) => b.rawClicks - a.rawClicks);
};
